// packages/api/src/hooks/admin/useVehicleCatalog.ts
// Phase 02.3 P05 — Admin Vehicle Catalog hooks (TanStack Query).

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';

import type { ApiSuccess } from '@alamo/types';

import { apiClient } from '../../client';

// ─── Types (inline; promote to @alamo/types when shared by other consumers) ──

export interface VehicleCategory {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  /**
   * "Os modelos desta categoria vêm da tabela FIPE oficial" — nível
   * CATEGORIA. NÃO confundir com `VehicleModel.hasFipe`, que responde "este
   * modelo específico tem valor FIPE". Categorias sem FIPE (Bike, Scooter,
   * Jet Ski, Colecionador) não têm sync automática e nascem com faixa de
   * valor declarada.
   *
   * Opcional enquanto a coluna `vehicle_categories.has_fipe` não está
   * aplicada em todos os ambientes — consumers usam `categoryHasFipe()`,
   * que respeita o valor explícito e só cai no fallback por code quando vem
   * `undefined`.
   */
  hasFipe?: boolean;
  /** Opt-in para exibir e aceitar a categoria na jornada sem FIPE. */
  allowWithoutFipeQuotation?: boolean;
  /**
   * De-para com o `codigo_tipo_veiculo` do Hinova SGA. Só dígitos (1-10).
   * `null` quando a categoria ainda não tem correspondência no vendor — o
   * contrato então é transmitido sem `codigo_tipo_veiculo`.
   */
  sgaCode?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface VehicleSubgroup {
  id: string;
  categoryId: string;
  code: string;
  name: string;
  description: string | null;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * ESCR-194.2: brand não tem mais `categoryId` próprio. Categorias atendidas
 * são derivadas dos `vehicle_models` da brand (DISTINCT model.categoryId
 * JOIN vehicle_categories) e expostas via `categoriesAttended[]` na listagem
 * e no `findOne`.
 *
 * Endpoints de write (POST/PUT) NÃO retornam `categoriesAttended` (registro
 * recém-criado/atualizado pode não ter models ainda) — portanto o campo é
 * opcional no tipo.
 */
export interface VehicleCategoryRef {
  id: string;
  name: string;
  /** 'carro' | 'moto' | 'caminhao' | 'bike_eletrica' | 'scooter_eletrico' | 'outros' */
  code: string;
}

export interface VehicleBrand {
  id: string;
  name: string;
  externalId: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  /**
   * Presente em GET /brands e GET /brands/:id (sempre, [] quando sem models).
   * Ausente em POST/PUT response (write não enricha — invalidar cache de
   * listagem após mutate para refrescar chips).
   */
  categoriesAttended?: VehicleCategoryRef[];
}

export interface VehicleModelVariantSummary {
  id: string;
  modelId: string;
  yearModel: number;
  fuel: string;
  isZeroKm: boolean;
  currentReferenceValue: string;
  currentReferenceMonth: string | null;
  manufactureYearMin: number | null;
  manufactureYearMax: number | null;
}

export interface VehicleModel {
  id: string;
  brandId: string;
  /** Backend popula via JOIN com vehicle_brands.name (PR pricing fix 2026-05-12). */
  brandName?: string | null;
  categoryId?: string;
  /** Backend popula via JOIN com vehicle_categories.name (pricing fix 2026-05-12). */
  categoryName?: string | null;
  /** Backend popula via JOIN com vehicle_categories.code. */
  categoryCode?: string | null;
  /** NULLABLE: modelo pode existir sem subgrupo (fila de classificação FIPE). */
  subgroupId: string | null;
  subgroupName?: string | null;
  subgroupCode?: string | null;
  name: string;
  fipeCode: string | null;
  /**
   * Phase 02.3.5: year/fuel/referenceValue migraram para
   * vehicle_model_variants. Backend retorna esses campos como compat
   * (derivados de latestVariant) durante a transição.
   */
  year: number | null;
  fuel: string | null;
  referenceValue: string | null;
  latestVariant?: VehicleModelVariantSummary | null;
  externalSource: string;
  isClassified: boolean;
  classifiedAt: string | null;
  classifiedBy: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface VehicleCatalogStats {
  modelsCataloged: number;
  variantsCataloged: number;
  activeSubgroups: number;
  fipeQueueCount: number;
  lastSyncAt: string | null;
  currentReferenceMonth: string | null;
}

export interface SyncHistoryItem {
  id: string;
  startedAt: string;
  finishedAt: string | null;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  imported: number;
  updated: number;
  archived: number;
  errors: number;
}

export interface PaginatedMeta {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

// ─── Vehicle Model Variants (ESCR-195.2 — CRUD master-detail) ────────────────

export type VehicleFuel =
  | 'gasolina'
  | 'etanol'
  | 'flex'
  | 'diesel'
  | 'eletrico'
  | 'hibrido'
  | 'gnv';

export const VEHICLE_FUELS: VehicleFuel[] = [
  'gasolina',
  'etanol',
  'flex',
  'diesel',
  'eletrico',
  'hibrido',
  'gnv',
];

export interface VehicleModelVariant {
  id: string;
  modelId: string;
  yearModel: number;
  fuel: VehicleFuel;
  isZeroKm: boolean;
  manufactureYearMin: number | null;
  manufactureYearMax: number | null;
  /** DECIMAL(12,2) serializado como string para preservar precisão. */
  currentReferenceValue: string;
  currentReferenceMonth: string | null;
  fuelCodeFipe: string | null;
  fipeYearCode: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * POST DTO. `yearModel` é OPCIONAL quando `isZeroKm=true` — backend
 * preenche com ano FIPE corrente (`fipe_reference_months ORDER BY year
 * DESC, month DESC LIMIT 1`; fallback `new Date().getFullYear()`).
 *
 * Backend aceita number (ou string parsable como number) em
 * `currentReferenceValue` — mantemos `number` no DTO para clareza.
 */
export interface CreateModelVariantDto {
  yearModel?: number;
  fuel: VehicleFuel;
  currentReferenceValue: number;
  isZeroKm?: boolean;
  manufactureYearMin?: number | null;
  manufactureYearMax?: number | null;
  currentReferenceMonth?: string | null;
  fuelCodeFipe?: string | null;
  fipeYearCode?: string | null;
}

export type UpdateModelVariantDto = Partial<CreateModelVariantDto>;

/**
 * Response body do DELETE — UAT-7 (Opção B) decidiu warning informativo
 * em vez de 409. Backend SEMPRE arquiva e retorna count de cotações
 * ativas referenciando a variante. Frontend usa pra modal de confirmação
 * pré-flight (ANTES de chamar DELETE) — endpoint próprio para count.
 */
export interface ArchiveModelVariantResponse {
  archived: true;
  activeQuotationsReferencing: number;
  warning?: string;
}

export interface ModelVariantsListResponse {
  count: number;
  rows: VehicleModelVariant[];
}

export interface ModelsListFilters {
  brandId?: string;
  categoryId?: string;
  subgroupId?: string;
  isClassified?: 'true' | 'false';
  includeArchived?: 'true' | 'false';
  /** ESCR-176.3 — true=FIPE oficial, false=cadastrado manualmente. */
  hasFipe?: 'true' | 'false';
  search?: string;
  page?: number;
  pageSize?: number;
}

// ─── Query keys ────────────────────────────────────────────────────────────

export const vehicleCatalogKeys = {
  all: ['admin', 'vehicle-catalog'] as const,
  stats: () => [...vehicleCatalogKeys.all, 'stats'] as const,
  categories: (publicMode?: boolean) =>
    [...vehicleCatalogKeys.all, 'categories', publicMode ? 'public' : 'auth'] as const,
  subgroups: (categoryId?: string) =>
    [...vehicleCatalogKeys.all, 'subgroups', { categoryId }] as const,
  brands: (categoryId?: string) => [...vehicleCatalogKeys.all, 'brands', { categoryId }] as const,
  models: (filters?: ModelsListFilters) =>
    [...vehicleCatalogKeys.all, 'models', filters ?? {}] as const,
  queue: (filters?: ModelsListFilters) =>
    [...vehicleCatalogKeys.all, 'queue', filters ?? {}] as const,
  /** Contagem leve da fila de classificação FIPE (badge do menu admin). */
  queueCount: () => [...vehicleCatalogKeys.all, 'queue', 'count'] as const,
  syncHistory: (page: number, pageSize: number) =>
    [...vehicleCatalogKeys.all, 'sync-history', { page, pageSize }] as const,
  /** ESCR-195.2 — variantes de um modelo. */
  modelVariants: (modelId: string | null, options?: { includeArchived?: boolean }) =>
    [
      ...vehicleCatalogKeys.all,
      'models',
      modelId,
      'variants',
      { includeArchived: !!options?.includeArchived },
    ] as const,
};

// ─── Stats ─────────────────────────────────────────────────────────────────

export function useVehicleCatalogStats() {
  return useQuery({
    queryKey: vehicleCatalogKeys.stats(),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<VehicleCatalogStats>>(
        '/admin/vehicle-catalog/stats',
      );
      return res.data.data;
    },
  });
}

// ─── Categories ────────────────────────────────────────────────────────────

// Backwards-compat shims — backend returns { count, rows } per
// PAGINATION-STANDARD.md. Pages still expect arrays; pass limit=100 (max)
// to keep the screens functional until proper pagination UI lands.

/**
 * `publicMode` (cotação pública /quote/:slug — sem auth) aponta para
 * GET /public-quotation/vehicle-categories (espelho @Public das categorias
 * ATIVAS do registro `vehicle_categories`, payload `{ code, name, isActive }[]`
 * já filtrado). O default (admin/consultor) usa o endpoint auth-only paginado
 * `{ count, rows }`. Mesma forma de retorno (`VehicleCategory[]`) nos dois modos.
 */
export function useVehicleCategories(options?: { enabled?: boolean; publicMode?: boolean }) {
  const publicMode = options?.publicMode ?? false;
  return useQuery({
    queryKey: vehicleCatalogKeys.categories(publicMode),
    queryFn: async () => {
      if (publicMode) {
        const res = await apiClient.get<ApiSuccess<VehicleCategory[]>>(
          '/public-quotation/vehicle-categories',
        );
        return res.data.data;
      }
      const res = await apiClient.get<ApiSuccess<{ count: number; rows: VehicleCategory[] }>>(
        '/admin/vehicle-catalog/categories',
        { params: { limit: 100 } },
      );
      return res.data.data.rows;
    },
    enabled: options?.enabled ?? true,
  });
}

// ─── Subgroups ─────────────────────────────────────────────────────────────

export function useVehicleSubgroups(categoryId?: string) {
  return useQuery({
    queryKey: vehicleCatalogKeys.subgroups(categoryId),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<{ count: number; rows: VehicleSubgroup[] }>>(
        '/admin/vehicle-catalog/subgroups',
        { params: { limit: 100, ...(categoryId ? { categoryId } : {}) } },
      );
      return res.data.data.rows;
    },
  });
}

/**
 * Variant paginada com server-side search — usada por pickers.
 *
 * Backend (`ListByCategoryQueryDto`) tem `@Max(100)` no limit. Para que
 * subgroups fora dos top-50 sejam encontráveis, passamos `search` e o
 * backend filtra ilike sobre `vehicle_subgroups.name`. Análogo a
 * `useVehicleBrandsAdmin`.
 */
export interface SubgroupsListFilters {
  page?: number;
  limit?: number;
  categoryId?: string;
  search?: string;
}

export function useVehicleSubgroupsAdmin(filters?: SubgroupsListFilters) {
  return useQuery({
    queryKey: [...vehicleCatalogKeys.all, 'subgroups-admin', filters ?? {}] as const,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<{ count: number; rows: VehicleSubgroup[] }>>(
        '/admin/vehicle-catalog/subgroups',
        { params: filters },
      );
      return res.data.data;
    },
    placeholderData: keepPreviousData,
  });
}

// ─── Brands ────────────────────────────────────────────────────────────────

export function useVehicleBrands(categoryId?: string) {
  return useQuery({
    queryKey: vehicleCatalogKeys.brands(categoryId),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<{ count: number; rows: VehicleBrand[] }>>(
        '/admin/vehicle-catalog/brands',
        { params: { limit: 100, ...(categoryId ? { categoryId } : {}) } },
      );
      return res.data.data.rows;
    },
  });
}

// ─── Models ────────────────────────────────────────────────────────────────

interface PaginatedModels {
  items: VehicleModel[];
  meta: PaginatedMeta;
}

export function useVehicleModels(filters?: ModelsListFilters) {
  return useQuery({
    queryKey: vehicleCatalogKeys.models(filters),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<PaginatedModels>>(
        '/admin/vehicle-catalog/models',
        { params: filters },
      );
      return res.data.data;
    },
    // ESCR-176.1 — mantém a tabela visivel durante refetch (mudança de pagina/busca)
    // em vez de piscar com spinner. Combinado com isFetching no consumer pra overlay.
    placeholderData: keepPreviousData,
  });
}

export function useVehicleQueue(filters?: ModelsListFilters) {
  return useQuery({
    queryKey: vehicleCatalogKeys.queue(filters),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<PaginatedModels>>('/admin/vehicle-catalog/queue', {
        params: filters,
      });
      return res.data.data;
    },
    placeholderData: keepPreviousData,
  });
}

/** Response de GET /admin/vehicle-catalog/queue/count. */
export interface VehicleCatalogQueueCount {
  count: number;
}

/**
 * Contagem leve da fila de classificação FIPE (badge do menu admin).
 * GET /admin/vehicle-catalog/queue/count — permission `pricing:manage`.
 * Espelha o padrão de `useSupportTicketsSummary` (query barata dedicada ao badge).
 * Contrato: docs/contracts/ESCR-fipe-import-classificacao.md.
 */
export function useVehicleQueueCount(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: vehicleCatalogKeys.queueCount(),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<VehicleCatalogQueueCount>>(
        '/admin/vehicle-catalog/queue/count',
      );
      return res.data.data;
    },
    enabled: options?.enabled ?? true,
  });
}

export function useClassifyModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, subgroupId }: { id: string; subgroupId: string }) => {
      const res = await apiClient.post<ApiSuccess<VehicleModel>>(
        `/admin/vehicle-catalog/models/${id}/classify`,
        { subgroupId },
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vehicleCatalogKeys.all });
    },
  });
}

/**
 * W8 (plano v4 §6.3) — Bulk classify de até 100 modelos em transação.
 * Backend (W3) emite audit_log por modelo + invalida cache wdapi2 paralelo.
 * Falhas individuais (já classificado, lock conflict) vão pra `errors[]`.
 */
export interface BulkClassifyInput {
  modelIds: string[];
  subgroupId: string;
  reason: string;
}

export interface BulkClassifyResult {
  classified: number;
  skipped: number;
  errors: Array<{ modelId: string; reason: string }>;
}

export function useBulkClassify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: BulkClassifyInput) => {
      const res = await apiClient.post<ApiSuccess<BulkClassifyResult>>(
        '/admin/vehicle-catalog/bulk-classify',
        input,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vehicleCatalogKeys.all });
    },
  });
}

// Wave 2 v2 — cadastro manual de modelo SEM FIPE (QA-030 caso A)

/**
 * ESCR-176.7 — combustíveis aceitos pela coluna vehicle_model_variants.fuel
 * (CHECK constraint). Manter alinhado com vmv_fuel_valid no backend.
 */
export type VehicleVariantFuel =
  | 'gasolina'
  | 'etanol'
  | 'flex'
  | 'diesel'
  | 'eletrico'
  | 'hibrido'
  | 'gnv';

export interface ManualVehicleModelVariantInput {
  yearModel: number;
  fuel: VehicleVariantFuel;
  /** Em Reais (R$). Backend aceita number e serializa como DECIMAL(12,2). */
  currentReferenceValue: number;
  isZeroKm?: boolean;
}

export interface CreateManualVehicleModelPayload {
  brandId: string;
  subgroupId: string;
  name: string;
  /**
   * Obrigatório: o SGA/Hinova exige `codigo_fipe` no contrato. Sem FIPE
   * oficial o admin informa um código próprio (1-20 chars, sem formato
   * fixo) e cadastra O MESMO código no SGA, manualmente. Único globalmente
   * entre modelos ativos — colisão devolve 409
   * `VEHICLE_MODEL_FIPE_CODE_DUPLICATE`.
   */
  fipeCode: string;
  manualValueRangeMin: number;
  manualValueRangeMax: number;
  fipeOverrideReason: string;
  exceptionType:
    | 'placa_preta'
    | 'eletrico'
    | 'homemade'
    | 'antigo'
    | 'carreta_sem_fipe'
    | 'colecionador'
    | 'bike_eletrica'
    | 'scooter_eletrico'
    | 'patinete_eletrico';
  /**
   * ESCR-176.7 — cadastra ano × combustível × valor junto com o modelo em
   * transação única (max 50, combinação (yearModel, fuel) única por array).
   * Opcional no backend; a UI exige pelo menos uma porque modelo sem
   * variante não cota (`VARIANT_NOT_FOUND` 422 no motor).
   */
  variants?: ManualVehicleModelVariantInput[];
}

export type FipeLookupVehicleType = 'cars' | 'motorcycles' | 'trucks';

export interface FipeLookupYear {
  /** Código ano-combustível da FIPE, ex.: "2027-1". */
  yearCode: string;
  yearModel: number;
  isZeroKm: boolean;
  fuel: VehicleVariantFuel;
  /** Grafia da FIPE, para exibição ("Gasolina"). */
  fuelLabel: string;
  /** Decimal serializado, ex.: "31344.00". */
  value: string;
}

export interface FipeLookupFound {
  found: true;
  fipeCode: string;
  vehicleType: FipeLookupVehicleType;
  brand: string;
  model: string;
  referenceCode: number;
  referenceLabel: string;
  years: FipeLookupYear[];
}

export type FipeLookupResult = FipeLookupFound | { found: false; fipeCode: string };

/**
 * Consulta um código na tabela FIPE oficial sem gravar nada.
 *
 * Mutation, não query: a consulta é uma ação do admin (clicou em buscar), não
 * um dado que a tela precisa ter carregado. `found: false` é resposta válida —
 * significa "veículo sem FIPE", que segue para o cadastro manual.
 */
export function useFipeLookup() {
  return useMutation({
    mutationFn: async (params: {
      fipeCode: string;
      vehicleType?: FipeLookupVehicleType;
    }): Promise<FipeLookupResult> => {
      const res = await apiClient.get<ApiSuccess<FipeLookupResult>>(
        '/admin/vehicle-catalog/models/fipe-lookup',
        { params },
      );
      return res.data.data;
    },
  });
}

export interface ImportFipeModelPayload {
  brandId: string;
  subgroupId: string;
  name: string;
  fipeCode: string;
  referenceLabel: string;
  years: FipeLookupYear[];
}

/**
 * Cria o modelo a partir do que a FIPE devolveu.
 *
 * Usa `POST /models` (rota de origem técnica), não a manual: o modelo nasce
 * `has_fipe=true`, com valor e competência da fonte — é o que separa "importado"
 * de "digitado". Zero quilômetro vai sem `yearModel`: o código 32000 da FIPE
 * está fora do range aceito, e o backend preenche com o ano vigente.
 */
export function useImportVehicleModelFromFipe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ImportFipeModelPayload) => {
      const res = await apiClient.post<ApiSuccess<VehicleModel>>('/admin/vehicle-catalog/models', {
        brandId: payload.brandId,
        subgroupId: payload.subgroupId,
        name: payload.name,
        fipeCode: payload.fipeCode,
        externalSource: 'parallelum',
        isClassified: true,
        variants: payload.years.map((year) => ({
          ...(year.isZeroKm ? { isZeroKm: true } : { yearModel: year.yearModel }),
          fuel: year.fuel,
          currentReferenceValue: Number(year.value),
          currentReferenceMonth: payload.referenceLabel,
          fipeYearCode: year.yearCode,
        })),
      });
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vehicleCatalogKeys.all });
    },
  });
}

export function useCreateManualVehicleModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateManualVehicleModelPayload) => {
      const res = await apiClient.post<ApiSuccess<VehicleModel>>(
        '/admin/vehicle-catalog/models/manual',
        dto,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vehicleCatalogKeys.all });
    },
  });
}

export function useArchiveModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const res = await apiClient.post<ApiSuccess<VehicleModel>>(
        `/admin/vehicle-catalog/models/${id}/archive`,
        { reason },
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vehicleCatalogKeys.all });
    },
  });
}

// ─── Model Variants CRUD (ESCR-195.2) ─────────────────────────────────────

/**
 * GET /admin/vehicle-catalog/models/:modelId/variants
 *
 * Lista variantes de um modelo. `includeArchived=true` traz arquivadas
 * (default false). Resposta `{ count, rows }` ordenada por yearModel desc,
 * fuel asc.
 */
export function useModelVariants(
  modelId: string | null | undefined,
  options?: { includeArchived?: boolean },
) {
  return useQuery({
    queryKey: vehicleCatalogKeys.modelVariants(modelId ?? null, options),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<ModelVariantsListResponse>>(
        `/admin/vehicle-catalog/models/${modelId}/variants`,
        {
          params: options?.includeArchived ? { includeArchived: true } : undefined,
        },
      );
      return res.data.data;
    },
    enabled: !!modelId,
  });
}

function invalidateVariantsAndModels(qc: ReturnType<typeof useQueryClient>, modelId: string): void {
  // Refresh master-detail (todas as variantes do modelo, com/sem archived)
  qc.invalidateQueries({
    queryKey: [...vehicleCatalogKeys.all, 'models', modelId, 'variants'],
  });
  // Refresh listagem master — `latestVariant` exibido na coluna pode ter mudado
  qc.invalidateQueries({ queryKey: [...vehicleCatalogKeys.all, 'models'] });
}

/**
 * POST /admin/vehicle-catalog/models/:modelId/variants
 *
 * Cria variante. Em modo 0 KM, `yearModel` pode ser omitido — backend
 * preenche com ano FIPE corrente. Erro 409 `PRICING_VARIANT_DUPLICATE`
 * quando (modelId, yearModel, fuel) já existe ativa.
 */
export function useCreateModelVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ modelId, dto }: { modelId: string; dto: CreateModelVariantDto }) => {
      const res = await apiClient.post<ApiSuccess<VehicleModelVariant>>(
        `/admin/vehicle-catalog/models/${modelId}/variants`,
        dto,
      );
      return res.data.data;
    },
    onSuccess: (_data, { modelId }) => {
      invalidateVariantsAndModels(qc, modelId);
    },
  });
}

/**
 * PUT /admin/vehicle-catalog/variants/:variantId — partial update.
 *
 * `modelId` NÃO é editável (backend ignora se vier no body). Mudança de
 * yearModel/fuel pode causar 409 se colidir com outra variant ativa.
 */
export function useUpdateModelVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      variantId,
      modelId: _modelId,
      dto,
    }: {
      variantId: string;
      /** Necessário só pra invalidação de cache — backend ignora. */
      modelId: string;
      dto: UpdateModelVariantDto;
    }) => {
      const res = await apiClient.put<ApiSuccess<VehicleModelVariant>>(
        `/admin/vehicle-catalog/variants/${variantId}`,
        dto,
      );
      return res.data.data;
    },
    onSuccess: (_data, { modelId }) => {
      invalidateVariantsAndModels(qc, modelId);
    },
  });
}

/**
 * DELETE /admin/vehicle-catalog/variants/:variantId — soft delete.
 *
 * UAT-7 (Opção B): backend SEMPRE arquiva; retorna 200 com body
 * `{archived, activeQuotationsReferencing, warning?}`. Frontend pode
 * mostrar modal de confirmação pré-flight quando `count > 0`.
 */
export function useArchiveModelVariant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      variantId,
      modelId: _modelId,
    }: {
      variantId: string;
      /** Necessário só pra invalidação. */
      modelId: string;
    }) => {
      const res = await apiClient.delete<ApiSuccess<ArchiveModelVariantResponse>>(
        `/admin/vehicle-catalog/variants/${variantId}`,
      );
      return res.data.data;
    },
    onSuccess: (_data, { modelId }) => {
      invalidateVariantsAndModels(qc, modelId);
    },
  });
}

// ─── Subgroups CRUD (gap follow-up — backend já existia) ──────────────────

export interface UpsertSubgroupPayload {
  categoryId: string;
  code: string;
  name: string;
  description?: string;
  displayOrder?: number;
  isActive?: boolean;
}

/**
 * POST /admin/vehicle-catalog/subgroups — cria subgrupo. `categoryId` é
 * obrigatório no create. Backend valida `code` único por categoria.
 */
export function useCreateSubgroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: UpsertSubgroupPayload) => {
      const res = await apiClient.post<ApiSuccess<VehicleSubgroup>>(
        '/admin/vehicle-catalog/subgroups',
        dto,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vehicleCatalogKeys.all });
    },
  });
}

/**
 * PUT /admin/vehicle-catalog/subgroups/:id — atualiza subgrupo. `categoryId`
 * é IGNORADO no PUT (subgrupo pertence a uma categoria fixa — mover entre
 * categorias quebra FK em vehicle_models).
 */
export function useUpdateSubgroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: UpsertSubgroupPayload }) => {
      const res = await apiClient.put<ApiSuccess<VehicleSubgroup>>(
        `/admin/vehicle-catalog/subgroups/${id}`,
        dto,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vehicleCatalogKeys.all });
    },
  });
}

/**
 * DELETE /admin/vehicle-catalog/subgroups/:id — apaga subgrupo. Backend
 * rejeita 409 PRICING_SUBGROUP_HAS_MODELS se houver modelos vinculados.
 */
export function useDeleteSubgroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/admin/vehicle-catalog/subgroups/${id}`);
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vehicleCatalogKeys.all });
    },
  });
}

// ─── Brands CRUD (ESCR-191.4 → refactor ESCR-194) ─────────────────────────

/**
 * ESCR-194.2: body sem `categoryId`. Categoria de uma brand é derivada
 * dos models cadastrados (vide `categoriesAttended` em VehicleBrand).
 */
export interface UpsertBrandPayload {
  name: string;
  /**
   * ID externo (Parallelum) — preenchido apenas pra marcas vindas de FIPE
   * sync. Frontend mostra como origem na listagem (FIPE vs Manual).
   */
  externalId?: string;
  isActive?: boolean;
}

/**
 * Filtro `categoryId` ainda funciona via subquery server-side: brand entra
 * na resposta apenas se tem ≥1 model ativo nessa categoria. Usado por
 * pickers contextuais (ex: cotação restrita a categoria).
 */
export interface BrandsListFilters {
  page?: number;
  limit?: number;
  categoryId?: string;
  search?: string;
}

interface PaginatedBrands {
  count: number;
  rows: VehicleBrand[];
}

/**
 * Variant paginada (admin) — útil para tela de listagem com paginação
 * server-side. O `useVehicleBrands` original retorna `T[]` com cap 100
 * (compat shim — usado em selects). Backend retorna `{count, rows}` per
 * PAGINATION-STANDARD.md.
 */
export function useVehicleBrandsAdmin(filters?: BrandsListFilters) {
  return useQuery({
    queryKey: [...vehicleCatalogKeys.all, 'brands-admin', filters ?? {}] as const,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<PaginatedBrands>>(
        '/admin/vehicle-catalog/brands',
        { params: filters },
      );
      return res.data.data;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * ESCR-194.2: GET /admin/vehicle-catalog/brands/:id (findOne) com
 * `categoriesAttended[]` enriquecido. Usado pelo modal de edit pra exibir
 * cabeçalho com chips das categorias atendidas sem precisar passar a
 * brand inteira via prop.
 */
export function useBrand(id: string | undefined) {
  return useQuery({
    queryKey: [...vehicleCatalogKeys.all, 'brand', id] as const,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<VehicleBrand>>(
        `/admin/vehicle-catalog/brands/${id}`,
      );
      return res.data.data;
    },
    enabled: !!id,
  });
}

/**
 * ESCR-194.4: GET /admin/vehicle-catalog/brands/:id/categories. Endpoint
 * de conveniência pra dropdowns/chips de categorias quando o frontend já
 * tem o brand id em cache. Cache 60s — categorias mudam pouco.
 */
export function useBrandCategories(id: string | undefined) {
  return useQuery({
    queryKey: [...vehicleCatalogKeys.all, 'brand', id, 'categories'] as const,
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<VehicleCategoryRef[]>>(
        `/admin/vehicle-catalog/brands/${id}/categories`,
      );
      return res.data.data;
    },
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useCreateBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: UpsertBrandPayload) => {
      const res = await apiClient.post<ApiSuccess<VehicleBrand>>(
        '/admin/vehicle-catalog/brands',
        dto,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vehicleCatalogKeys.all });
    },
  });
}

export function useUpdateBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: UpsertBrandPayload }) => {
      const res = await apiClient.put<ApiSuccess<VehicleBrand>>(
        `/admin/vehicle-catalog/brands/${id}`,
        dto,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vehicleCatalogKeys.all });
    },
  });
}

export function useDeleteBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/admin/vehicle-catalog/brands/${id}`);
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vehicleCatalogKeys.all });
    },
  });
}

// ─── Categories CRUD (ESCR-191.3) ─────────────────────────────────────────

export interface UpsertCategoryPayload {
  /** Code é IMUTÁVEL após criação (regex /^[a-z][a-z0-9_]{1,29}$/). */
  code: string;
  name: string;
  isActive?: boolean;
  /**
   * De-para com o `codigo_tipo_veiculo` do SGA — só dígitos (1-10).
   * Ausente no PUT PRESERVA o valor gravado; `null` LIMPA o de-para.
   */
  sgaCode?: string | null;
  /**
   * "Os modelos desta categoria vêm da FIPE" — nível CATEGORIA.
   * Ausente no PUT PRESERVA o valor. `null` é recusado com 400 (coluna
   * `NOT NULL`), por isso o tipo não admite null.
   */
  hasFipe?: boolean;
  /** So pode ser true quando `hasFipe=false`. */
  allowWithoutFipeQuotation?: boolean;
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: UpsertCategoryPayload) => {
      const res = await apiClient.post<ApiSuccess<VehicleCategory>>(
        '/admin/vehicle-catalog/categories',
        dto,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vehicleCatalogKeys.all });
    },
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: Omit<UpsertCategoryPayload, 'code'> }) => {
      const res = await apiClient.put<ApiSuccess<VehicleCategory>>(
        `/admin/vehicle-catalog/categories/${id}`,
        dto,
      );
      return res.data.data;
    },
    // Mantém a mutation pendente até a listagem ativa ser recarregada. O modal
    // fecha no callback do consumer; sem aguardar, reabrir imediatamente usa a
    // categoria antiga que ainda está guardada no estado `editing` da tabela.
    onSuccess: () => qc.invalidateQueries({ queryKey: vehicleCatalogKeys.all }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/admin/vehicle-catalog/categories/${id}`);
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vehicleCatalogKeys.all });
    },
  });
}

// ─── Model create (rota técnica de integração/import) ─────────────────────

export interface CreateModelPayload {
  brandId: string;
  subgroupId: string;
  name: string;
  fipeCode?: string;
  externalSource?: 'parallelum' | 'manual' | 'xlsx_import' | 'legacy' | 'wdapi2' | 'fipe_csv';
  isClassified?: boolean;
  /**
   * ESCR-195.5 (2026-05-13) — variantes opcionais criadas em transação única
   * junto com o modelo. Até 50 itens. Sem `variants`, comportamento idêntico
   * ao anterior. Backend retorna apenas o modelo; variantes ficam disponíveis
   * via GET /models/:id/variants após sucesso (invalidação de cache cobre).
   */
  variants?: CreateModelVariantDto[];
}

/**
 * POST /admin/vehicle-catalog/models — rota técnica para modelos com
 * referência FIPE oficial. O backend rejeita origem ausente ou `manual`.
 *
 * Cadastro feito por usuário usa `useCreateManualVehicleModel`: o código
 * próprio continua obrigatório para o SGA, mas `hasFipe` permanece false.
 */
export function useCreateModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dto: CreateModelPayload) => {
      const res = await apiClient.post<ApiSuccess<VehicleModel>>(
        '/admin/vehicle-catalog/models',
        dto,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vehicleCatalogKeys.all });
    },
  });
}

/**
 * Update DTO — backend `UpdateModelDto` é PartialType<OmitType<CreateModelDto,
 * 'brandId'>>. brandId é IMUTÁVEL pra preservar histórico FIPE.
 *
 * Não inclui manual_value_range_min/max nem campos de variant (year, fuel,
 * referenceValue) — gap backend conhecido.
 */
export interface UpdateModelPayload {
  subgroupId?: string;
  name?: string;
  fipeCode?: string;
  externalSource?: 'parallelum' | 'manual' | 'xlsx_import' | 'legacy' | 'wdapi2' | 'fipe_csv';
  isClassified?: boolean;
}

/**
 * PUT /admin/vehicle-catalog/models/:id — edita modelo. brandId imutável
 * (mudar quebra histórico FIPE — admin deve arquivar + recriar).
 */
export function useUpdateModel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, dto }: { id: string; dto: UpdateModelPayload }) => {
      const res = await apiClient.put<ApiSuccess<VehicleModel>>(
        `/admin/vehicle-catalog/models/${id}`,
        dto,
      );
      return res.data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: vehicleCatalogKeys.all });
    },
  });
}

// ─── Sync History (Tab 4 prototype-v2) ─────────────────────────────────────

interface PaginatedSyncHistory {
  items: SyncHistoryItem[];
  meta: PaginatedMeta;
}

export function useSyncHistory(page = 1, pageSize = 20) {
  return useQuery({
    queryKey: vehicleCatalogKeys.syncHistory(page, pageSize),
    queryFn: async () => {
      const res = await apiClient.get<ApiSuccess<PaginatedSyncHistory>>(
        '/admin/vehicle-catalog/sync-history',
        { params: { page, pageSize } },
      );
      return res.data.data;
    },
  });
}
